import { message, Spin } from "antd";
import Title from "antd/lib/typography/Title";
import React, { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { BUTTON_SIZE, INPUT_SIZE } from "../../../common/ThemeConstants";
import FormButton from "../../../components/general/FormButton";
import FormContainer from "../../../components/general/FormContainer";
import FormSelect from "../../../components/general/FormSelect";
import FormTextField from "../../../components/general/FormTextField";
import ReportExcelDownload from "../../../components/ReportingComponents/ReportExcelDownload";
import { postRequest } from "../../../services/mainApp.service";
import { getDate } from "../../../functions/dateFunctions";
import TransferSummaryTemp from "./TransferSummaryTemp";
import { SearchOutlined } from "@ant-design/icons";
import { TRANSFER } from "../../../common/InventoryEnum";

const initialSearchValues = {
  OperationId: 1,
  CompanyId: null,
  BranchId: null,
  DepartmentId: null,
  DataType: null,
  DateFrom: "",
  DateTo: "",
  TransactionTypeId: TRANSFER,
  UserId: null,
};

const TransferSummary = () => {
  const controller = new window.AbortController();
  const userData = useSelector((state) => state.authReducer);
  const [data, setData] = useState(initialSearchValues);
  const [list, setList] = useState([]);
  const [branchList, setBranchList] = useState([]);
  const [departmentList, setDepartmentList] = useState([]);
  const [disableExcel, setDisableExcel] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    postRequest(
      "/Inv_TransactionSummary",
      {
        OperationId: 1,
        CompanyId: userData.CompanyId,
        BranchId: null,
        DepartmentId: null,
        DataType: null,
        DateFrom: data.DateFrom || getDate(),
        DateTo: data.DateTo || getDate(),
        TransactionTypeId: TRANSFER,
        UserId: userData.UserId,
      },
      controller
    )
      .then((res) => {
        if (res.error) {
          message.error(res.errorMessage || "Failed to fetch dropdown data");
          return;
        }
        const additionalData = res?.data?.AdditionalData || {};
        const dataSet = res?.data?.DataSet || {};
        setBranchList(additionalData.Branches || dataSet.Table || []);
        setDepartmentList(additionalData.Departments || dataSet.Table1 || []);
        setData((prev) => ({
          ...prev,
          DateFrom: prev.DateFrom || getDate(),
          DateTo: prev.DateTo || getDate(),
        }));
      })
      .catch((err) => {
        if (err.message !== "canceled") {
          console.error(err);
          message.error("Failed to fetch dropdown data");
        }
      });
    return () => controller.abort();
  }, []);

  const handleSearchChange = (data) => {
    setData((prev) => ({ ...prev, [data.name]: data.value }));
    setList([]);
    setDisableExcel(true);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (!data.DateFrom || !data.DateTo) {
      message.error("Please select Date From and Date To");
      return;
    }
    if (new Date(data.DateFrom) > new Date(data.DateTo)) {
      message.error("Date From must be less than or equal to Date To");
      return;
    }
    setLoading(true);
    postRequest("/Inv_TransactionSummary", {
      OperationId: 1,
      CompanyId: userData.CompanyId,
      BranchId: data.BranchId ?? null,
      DepartmentId: data.DepartmentId ?? null,
      DataType: "TransferSummary",
      DateFrom: data.DateFrom || getDate(),
      DateTo: data.DateTo || getDate(),
      TransactionTypeId: data.TransactionTypeId ?? TRANSFER,
      UserId: userData.UserId,
    })
      .then((res) => {
        setLoading(false);
        if (res.error) {
          message.error(res.errorMessage || "Failed to fetch report");
          setList([]);
          setDisableExcel(true);
          return;
        }
        const reportData = res?.data?.Data || res?.data?.DataSet?.Table || [];
        if (!reportData.length) {
          message.error("No records found!");
          setList([]);
          setDisableExcel(true);
          return;
        }
        setList(reportData);
        setDisableExcel(false);
      })
      .catch((err) => {
        setLoading(false);
        if (err.message !== "canceled") {
          console.error(err);
          message.error("Failed to fetch report");
        }
        setList([]);
        setDisableExcel(true);
      });
  };

  const searchPanel = (
    <Fragment>
      <FormSelect
        colSpan={6}
        listItem={branchList || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="BranchId"
        label="Branch"
        value={data.BranchId}
        onChange={handleSearchChange}
        allowClear={true}
      />
      <FormSelect
        colSpan={6}
        listItem={departmentList || []}
        idName="Id"
        valueName="Name"
        size={INPUT_SIZE}
        name="DepartmentId"
        label="Department"
        value={data.DepartmentId}
        onChange={handleSearchChange}
        allowClear={true}
      />
      <FormTextField
        colSpan={6}
        label="Date From"
        type="date"
        name="DateFrom"
        size={INPUT_SIZE}
        value={data.DateFrom}
        onChange={handleSearchChange}
      />
      <FormTextField
        colSpan={6}
        label="Date To"
        type="date"
        name="DateTo"
        size={INPUT_SIZE}
        value={data.DateTo}
        onChange={handleSearchChange}
      />
      <FormButton
        title="Search"
        type="primary"
        size={BUTTON_SIZE}
        colSpan={2}
        htmlType="submit"
        icon={<SearchOutlined />}
      />
    </Fragment>
  );

  return (
    <div style={{ margin: 5 }}>
      <Title level={3} style={{ color: "#017971" }}>
        Transfer Summary
      </Title>
      <div
        style={{
          background: "white",
          padding: 15,
          boxShadow: "0 0 5px lightgray",
          borderRadius: 2,
        }}
      >
        <FormContainer
          rowStyle={{ alignItems: "flex-end" }}
          onSubmit={handleSearchSubmit}
        >
          {searchPanel}
        </FormContainer>
        <div style={{ margin: "10px 0", borderTop: "1px solid lightgray" }}></div>
      </div>
      <ReportExcelDownload
        fileName={`TransferSummary_Report_${data.DateFrom}_${data.DateTo}`}
      >
        {loading && (
          <Spin
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
            }}
          />
        )}
        {list.length > 0 && (
          <TransferSummaryTemp
            list={list}
            headList={list.length > 0 ? Object.keys(list[0]) : []}
            date={`From Date: ${data.DateFrom} To Date: ${data.DateTo}`}
            branch={
              branchList?.find(
                (x) => x.Id === data.BranchId || x.BranchId === data.BranchId
              )?.Name ||
              branchList?.find(
                (x) => x.Id === data.BranchId || x.BranchId === data.BranchId
              )?.BranchName ||
              "All"
            }
            department={
              departmentList?.find((x) => x.Id === data.DepartmentId)?.Name ||
              departmentList?.find((x) => x.Id === data.DepartmentId)?.DepartmentName ||
              "All"
            }
          />
        )}
      </ReportExcelDownload>
    </div>
  );
};

export default TransferSummary;
